import { isDefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Stack, TextField } from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { z, type ZodSchema } from "zod";

interface SaveableTextFieldProps {
  label: string;
  value: string;
  placeholder?: string;
  schema: ZodSchema<string>;
  onSave: (value: string) => void;
  disabled?: boolean;
}

interface FormValues {
  value: string;
}

export function SaveableTextField(props: SaveableTextFieldProps) {
  const { label, value, placeholder, schema, onSave, disabled = false } = props;

  const formSchema = useMemo(() => z.object({ value: schema }), [schema]);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    reset,
    trigger,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { value },
    mode: "onChange",
  });

  useEffect(() => {
    reset({ value });
  }, [value, reset]);

  // Validate on mount so errors (e.g. "Required") show immediately,
  // since mode: "onChange" only validates after user interaction.
  useEffect(() => {
    void trigger("value");
  }, [trigger]);

  function onSubmit(data: FormValues) {
    onSave(data.value);
    reset({ value: data.value });
  }

  const hasError = isDefined(errors.value);

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Text variant="body1" sx={{ minWidth: 130 }}>
        {label}
      </Text>
      <Controller
        name="value"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            size="small"
            placeholder={placeholder}
            disabled={disabled || isSubmitting}
            sx={{ width: 280 }}
          />
        )}
      />
      <Button
        variant="text"
        size="small"
        disabled={disabled || isSubmitting || hasError || !isDirty}
        onClick={handleSubmit(onSubmit)}
      >
        {isSubmitting ? "Saving..." : "Save"}
      </Button>
      {hasError && (
        <Text variant="body2" color="warning.main">
          {errors.value?.message}
        </Text>
      )}
    </Stack>
  );
}
